import React, { useMemo, useState } from "react";
import {
  ArrowUp,
  ArrowDown,
  ArrowsDownUp,
  ArrowSquareOut,
  Globe,
  Star,
  Sparkle,
  Note,
  Warning,
  ClockCountdown,
  Phone,
} from "@phosphor-icons/react";
import { StatusPill } from "./StatusPill";
import { LeadStatusBadge, callbackFlag } from "./LeadNotesDialog";
import { AIVisibilityBadge, AIPositionPill } from "./AIBadges";
import { fmtNumber, fmtRating, formatRelative } from "@/lib/format";

const COLUMNS = [
  { key: "title", label: "Business", w: "min-w-[240px]", align: "left" },
  { key: "rating", label: "Rating", w: "w-24", align: "right", numeric: true },
  { key: "reviews", label: "Reviews", w: "w-24", align: "right", numeric: true },
  { key: "total_posts", label: "Posts (page)", w: "w-24", align: "right", numeric: true },
  {
    key: "posts_this_month",
    label: "This month",
    w: "w-24",
    align: "right",
    numeric: true,
  },
  {
    key: "posts_last_month",
    label: "Last month",
    w: "w-24",
    align: "right",
    numeric: true,
  },
  {
    key: "last_post_date",
    label: "Last post",
    w: "w-32",
    align: "left",
    numeric: true,
  },
  {
    key: "posts_per_month",
    label: "Posts / mo",
    w: "w-24",
    align: "right",
    numeric: true,
  },
  { key: "activity_status", label: "Activity", w: "w-32", align: "left" },
  { key: "ai_visibility", label: "AI Visibility", w: "w-28", align: "left" },
  { key: "ai_position", label: "Google AI", w: "w-32", align: "left", sortable: false },
  { key: "remark", label: "Remarks", w: "min-w-[220px]", align: "left", sortable: false },
  { key: "draft", label: "", w: "w-20", align: "right", sortable: false },
  { key: "actions", label: "", w: "w-14", align: "right", sortable: false },
];

const STATUS_ORDER = { active: 3, low: 2, dormant: 1, none: 0 };

function sortRows(rows, key, dir) {
  const factor = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    let av = a[key];
    let bv = b[key];
    if (key === "activity_status") {
      av = STATUS_ORDER[av || "none"];
      bv = STATUS_ORDER[bv || "none"];
    }
    if (av === null || av === undefined || av === "") return 1;
    if (bv === null || bv === undefined || bv === "") return -1;
    if (typeof av === "string" && typeof bv === "string") {
      return av.localeCompare(bv) * factor;
    }
    return (av > bv ? 1 : av < bv ? -1 : 0) * factor;
  });
}

export function ResultsTable({ rows, total, progress, status, onDraft, leads = {}, onEditLead }) {
  const [sortKey, setSortKey] = useState("activity_status");
  const [sortDir, setSortDir] = useState("desc");

  const displayed = useMemo(() => sortRows(rows, sortKey, sortDir), [rows, sortKey, sortDir]);

  const clickSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "title" ? "asc" : "desc");
    }
  };

  const pendingRows = Math.max(0, (total || 0) - rows.length);
  const isScanning =
    status === "pending" || status === "discovering" || status === "fetching";

  return (
    <div
      data-testid="results-table-wrapper"
      className="overflow-hidden rounded-lg border border-border bg-white"
    >
      <div className="max-h-[70vh] overflow-auto">
        <table className="w-full min-w-[960px] border-collapse font-sans text-sm">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="border-b border-border">
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  className={`${c.w} px-3 py-2.5 text-left font-mono text-[11px] uppercase tracking-wider text-muted-foreground`}
                >
                  {c.sortable === false ? (
                    <span>{c.label}</span>
                  ) : (
                    <button
                      type="button"
                      data-testid={`sort-${c.key}`}
                      onClick={() => clickSort(c.key)}
                      className={`inline-flex items-center gap-1 hover:text-ink ${
                        c.align === "right" ? "justify-end w-full" : ""
                      }`}
                    >
                      <span>{c.label}</span>
                      {sortKey === c.key ? (
                        sortDir === "asc" ? (
                          <ArrowUp size={12} weight="bold" />
                        ) : (
                          <ArrowDown size={12} weight="bold" />
                        )
                      ) : (
                        <ArrowsDownUp size={12} className="opacity-40" />
                      )}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayed.map((r, i) => {
              const lead = leads[r.place_id] || null;
              const flag = callbackFlag(lead?.callback_at);
              const rowClass = flag === "overdue"
                ? "border-l-2 border-l-[#C92A2A]"
                : flag === "today"
                ? "border-l-2 border-l-[#B45309]"
                : "";
              return (
              <tr
                key={r.place_id || r.data_id || i}
                data-testid={`result-row-${i}`}
                className={`gbp-row-in border-b border-border/60 transition-colors hover:bg-electric-soft/30 even:bg-black/[0.015] ${rowClass}`}
              >
                <td className="px-3 py-3 align-top">
                  <div className="font-sans font-semibold text-ink">{r.title}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {r.address || "—"}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {r.category ? (
                      <span className="rounded-sm bg-black/5 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                        {r.category}
                      </span>
                    ) : null}
                    {r.phone ? (
                      <a
                        href={`tel:${r.phone.replace(/\s+/g, "")}`}
                        data-testid={`row-phone-${i}`}
                        className="inline-flex items-center gap-1 font-mono text-xs text-ink transition-colors hover:text-electric"
                        title="Click to call"
                      >
                        <Phone size={12} weight="bold" />
                        {r.phone}
                      </a>
                    ) : null}
                    {r.website ? (
                      <a
                        href={r.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`row-website-${i}`}
                        className="inline-flex items-center gap-1 text-xs text-electric hover:underline"
                      >
                        <Globe size={12} weight="bold" />
                        website
                      </a>
                    ) : null}
                  </div>
                </td>
                <td className="px-3 py-3 text-right align-top">
                  <span className="inline-flex items-center gap-1 font-mono tabular-nums text-ink">
                    <Star size={12} weight="fill" className="text-yellow-500" />
                    {fmtRating(r.rating)}
                  </span>
                </td>
                <td className="px-3 py-3 text-right align-top font-mono tabular-nums text-ink">
                  {fmtNumber(r.reviews)}
                </td>
                <td className="px-3 py-3 text-right align-top">
                  {r.fetch_status === "pending" ? (
                    <span className="inline-block h-3 w-10 rounded gbp-shimmer" />
                  ) : (
                    <span className="inline-flex items-center gap-1 font-mono tabular-nums text-ink">
                      {fmtNumber(r.total_posts)}
                      {r.posts_truncated ? (
                        <span
                          title="More posts exist — page 1 fetched to keep scans fast"
                          className="rounded-sm bg-black/5 px-1 font-mono text-[10px] font-medium text-muted-foreground"
                        >
                          +
                        </span>
                      ) : null}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 text-right align-top">
                  {r.fetch_status === "pending" ? (
                    <span className="inline-block h-3 w-10 rounded gbp-shimmer" />
                  ) : (
                    <span
                      className={`font-mono tabular-nums ${
                        (r.posts_this_month || 0) > 0
                          ? "font-semibold text-electric"
                          : "text-muted-foreground"
                      }`}
                    >
                      {fmtNumber(r.posts_this_month || 0)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 text-right align-top">
                  {r.fetch_status === "pending" ? (
                    <span className="inline-block h-3 w-10 rounded gbp-shimmer" />
                  ) : (
                    <span
                      className={`font-mono tabular-nums ${
                        (r.posts_last_month || 0) > 0
                          ? "font-medium text-ink"
                          : "text-muted-foreground"
                      }`}
                    >
                      {fmtNumber(r.posts_last_month || 0)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 align-top">
                  {r.fetch_status === "pending" ? (
                    <span className="inline-block h-3 w-16 rounded gbp-shimmer" />
                  ) : r.last_post_date ? (
                    <div className="font-mono text-xs text-ink">
                      <div>{formatRelative(r.last_post_date)}</div>
                      <div className="text-muted-foreground">
                        {new Date(r.last_post_date).toLocaleDateString()}
                      </div>
                      {r.first_post_date &&
                      r.first_post_date !== r.last_post_date ? (
                        <div className="mt-0.5 text-[10px] text-muted-foreground/80">
                          span since{" "}
                          {new Date(r.first_post_date).toLocaleDateString()}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-3 py-3 text-right align-top">
                  {r.fetch_status === "pending" ? (
                    <span className="inline-block h-3 w-10 rounded gbp-shimmer" />
                  ) : (
                    <span className="font-mono tabular-nums text-ink">
                      {r.posts_per_month || "0"}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 align-top">
                  {r.fetch_status === "pending" ? (
                    <span className="inline-block h-4 w-20 rounded-full gbp-shimmer" />
                  ) : (
                    <StatusPill status={r.activity_status || "none"} />
                  )}
                </td>
                <td className="px-3 py-3 align-top">
                  {r.fetch_status === "pending" ? (
                    <span className="inline-block h-4 w-16 rounded-full gbp-shimmer" />
                  ) : (
                    <AIVisibilityBadge
                      level={r.ai_visibility}
                      testid={`row-ai-visibility-${i}`}
                    />
                  )}
                </td>
                <td className="px-3 py-3 align-top">
                  <AIPositionPill
                    text={r.ai_position}
                    testid={`row-ai-position-${i}`}
                  />
                </td>
                <td className="px-3 py-3 align-top">
                  <button
                    type="button"
                    data-testid={`lead-notes-btn-${i}`}
                    onClick={() =>
                      onEditLead &&
                      onEditLead({ business: r, index: rows.indexOf(r), lead })
                    }
                    className="w-full rounded-md border border-dashed border-border bg-white px-2 py-1.5 text-left transition-colors hover:border-electric hover:bg-electric-soft/40"
                  >
                    <div className="flex items-center gap-2">
                      <LeadStatusBadge
                        status={lead?.status || "not_contacted"}
                        testid={`row-lead-status-${i}`}
                      />
                      {flag === "overdue" ? (
                        <span
                          data-testid={`callback-overdue-${i}`}
                          className="inline-flex items-center gap-1 rounded-full border border-[#FECACA] bg-[#FCE8E8] px-2 py-0.5 text-[10px] font-medium text-[#C92A2A]"
                          title="Callback overdue"
                        >
                          <Warning size={10} weight="fill" />
                          Overdue
                        </span>
                      ) : flag === "today" ? (
                        <span
                          data-testid={`callback-today-${i}`}
                          className="inline-flex items-center gap-1 rounded-full border border-[#FDE68A] bg-[#FEF3C7] px-2 py-0.5 text-[10px] font-medium text-[#92400E]"
                          title="Callback due today"
                        >
                          <ClockCountdown size={10} weight="fill" />
                          Today
                        </span>
                      ) : null}
                    </div>
                    {lead?.remark ? (
                      <div
                        data-testid={`row-lead-remark-${i}`}
                        className="mt-1 line-clamp-2 font-sans text-xs text-muted-foreground"
                        title={lead.remark}
                      >
                        {lead.remark}
                      </div>
                    ) : (
                      <div className="mt-1 flex items-center gap-1 font-sans text-xs text-muted-foreground">
                        <Note size={11} weight="bold" />
                        Add a note
                      </div>
                    )}
                    {lead?.callback_at ? (
                      <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                        Callback · {new Date(lead.callback_at).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    ) : null}
                  </button>
                </td>
                <td className="px-3 py-3 text-right align-top">
                  {r.fetch_status === "pending" ? null : (
                    <button
                      type="button"
                      data-testid={`draft-btn-${i}`}
                      onClick={() =>
                        onDraft && onDraft({ business: r, index: rows.indexOf(r) })
                      }
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-2 py-1 font-sans text-xs text-ink transition-colors hover:border-electric hover:text-electric"
                      title="Generate outreach pack for this business"
                    >
                      <Sparkle size={12} weight="fill" />
                      Draft
                    </button>
                  )}
                </td>
                <td className="px-3 py-3 text-right align-top">
                  {r.gbp_url ? (
                    <a
                      href={r.gbp_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`row-gbp-${i}`}
                      className="inline-flex items-center justify-center rounded-md border border-border bg-white p-1.5 text-muted-foreground transition-colors hover:border-electric hover:text-electric"
                      aria-label="Open GBP"
                    >
                      <ArrowSquareOut size={14} weight="bold" />
                    </a>
                  ) : null}
                </td>
              </tr>
              );
            })}
            {isScanning &&
              Array.from({ length: Math.min(pendingRows, 6) }).map((_, i) => (
                <tr
                  key={`skeleton-${i}`}
                  data-testid={`skeleton-row-${i}`}
                  className="border-b border-border/40"
                >
                  {COLUMNS.map((c) => (
                    <td key={c.key} className="px-3 py-3">
                      <span
                        className={`inline-block h-3 rounded gbp-shimmer ${
                          c.key === "title" ? "w-2/3" : "w-14"
                        }`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {displayed.length === 0 && !isScanning && (
        <div
          data-testid="empty-results"
          className="border-t border-border/60 px-6 py-16 text-center text-sm text-muted-foreground"
        >
          No businesses matched this query. Try a broader niche or nearby suburb.
        </div>
      )}
    </div>
  );
}
